import { AddOutlined, CheckCircleRounded, CloseOutlined, DoNotDisturbOnRounded, KeyOutlined, RouteOutlined, SaveOutlined, StraightenOutlined, TagOutlined } from "@mui/icons-material";
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, MenuItem, Paper,
    IconButton, Radio, RadioGroup, Stack, Tab, Tabs, TextField, Tooltip, Typography,
    Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { LocalizationProvider, MobileDatePicker, MobileTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { APIProvider, Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useRecoilValue } from "recoil";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { Toast } from "primereact/toast";
import { userAtom } from "../../atoms/atoms";
import { MENUS_ITENS } from "../../consts/consts";
import { GTR_MAPS_ID, GTR_MAPS_KEY } from "../../consts/runtimeConfig";
import { getApiErrorMessage, setToastRefToOpen } from "../../functions/functions";
import GridTemplate from "../../components/GridTemplate";
import Rmodal from "../../components/Rmodal";
import findAllVehiclesByCustomerIdAndSecretaryIdAndDepartmentIdListResponse from "../../resources/Api/vehicle/findAllVehiclesByCustomerIdAndSecretaryIdAndDepartmentIdListResponse";
import { PlannedRoute, PlannedRouteRequest, PlannedRouteSummary, routeApi, RouteSchedule } from "../../resources/Api/routeApi";
import { UserType, VehicleListType } from "../../types/types";
import RouteAddressAutocomplete, { RouteAddress } from "./RouteAddressAutocomplete";

type Search = { origin: string; destination: string; waypoints: string[]; sequence: number };
type RouteWaypoint = { id: number; address: RouteAddress };
const emptyAddress = (): RouteAddress => ({ text: "" });
const directionLocation = ({ text, coordinates }: RouteAddress): string =>
    coordinates ? `${coordinates.lat},${coordinates.lng}` : text.trim();
const weekdays = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const primaryButtonSx = { bgcolor: "#00B700", fontFamily: "Prometo", textTransform: "none", borderRadius: 2, "&:hover": { bgcolor: "#009900" } };
const modalFieldSx = {
    "& .MuiOutlinedInput-root": {
        minHeight: 56,
        borderRadius: 2,
        backgroundColor: "background.paper",
        "& fieldset": { borderColor: "rgba(0, 63, 74, 0.24)" },
        "&:hover fieldset": { borderColor: "#006C77" },
        "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(0, 183, 0, 0.12)" },
        "&.Mui-focused fieldset": { borderColor: "#00B700", borderWidth: 2 },
    },
    "& .MuiInputLabel-root": { fontFamily: "Prometo" },
};

const columns: GridColDef[] = [
    {
        field: "activated",
        headerName: "Status",
        width: 130,
        renderHeader: () => (
            <Chip
                sx={{ color: "#fff", background: "transparent", fontFamily: "Prometo" }}
                label="Status"
                icon={<KeyOutlined style={{ height: 30, width: 30, fill: "#fff" }} />}
            />
        ),
        renderCell: ({ row }) => (
            <Chip
                sx={{ background: "transparent" }}
                aria-label={row.activated ? "Ativa" : "Inativa"}
                icon={row.activated
                    ? <CheckCircleRounded className="activated" style={{ height: 28, width: 28 }} />
                    : <DoNotDisturbOnRounded className="deactivated" style={{ height: 28, width: 28 }} />}
            />
        ),
    },
    {
        field: "name",
        headerName: "Nome da rota",
        align: "left",
        width: 280,
        renderHeader: () => (
            <Chip
                sx={{ color: "#fff", background: "transparent", fontFamily: "Prometo" }}
                label="Nome da rota"
                icon={<RouteOutlined style={{ height: 30, width: 30, fill: "#fff" }} />}
            />
        ),
        renderCell: ({ row }) => <Chip sx={{ background: "transparent", fontFamily: "Prometo" }} label={row.name} />,
    },
    {
        field: "plannedDistanceMeters",
        headerName: "Distância prevista",
        align: "left",
        width: 230,
        renderHeader: () => (
            <Chip
                sx={{ color: "#fff", background: "transparent", fontFamily: "Prometo" }}
                label="Distância prevista"
                icon={<StraightenOutlined style={{ height: 30, width: 30, fill: "#fff" }} />}
            />
        ),
        renderCell: ({ row }) => (
            <Chip
                sx={{ background: "transparent", fontFamily: "Prometo" }}
                label={row.plannedDistanceMeters == null ? "—" : `${(row.plannedDistanceMeters / 1000).toFixed(1)} km`}
            />
        ),
    },
    {
        field: "version",
        headerName: "Versão",
        align: "left",
        width: 150,
        headerClassName: "last-column",
        renderHeader: () => (
            <Chip
                sx={{ color: "#fff", background: "transparent", fontFamily: "Prometo" }}
                label="Versão"
                icon={<TagOutlined style={{ height: 30, width: 30, fill: "#fff" }} />}
            />
        ),
        renderCell: ({ row }) => <Chip sx={{ background: "transparent", fontFamily: "Prometo" }} label={row.version} />,
    },
];

function MapRoutes({ search, onRoutes, selectedIndex, savedPolyline, editable }: {
    search: Search;
    onRoutes: (routes: google.maps.DirectionsRoute[], selectedIndex?: number) => void;
    selectedIndex: number;
    savedPolyline: string | null;
    editable: boolean;
}) {
    const map = useMap();
    const routesLibrary = useMapsLibrary("routes");
    const geometry = useMapsLibrary("geometry");
    const [renderer, setRenderer] = useState<google.maps.DirectionsRenderer | null>(null);

    useEffect(() => {
        if (!map || !routesLibrary) return;
        const nextRenderer = new routesLibrary.DirectionsRenderer({ map, draggable: false });
        setRenderer(nextRenderer);
        return () => nextRenderer.setMap(null);
    }, [map, routesLibrary]);

    useEffect(() => { renderer?.setOptions({ draggable: editable }); }, [renderer, editable]);

    useEffect(() => {
        if (!renderer || search.sequence === 0) return;
        const listener = renderer.addListener("directions_changed", () => {
            const result = renderer.getDirections();
            if (result?.routes?.length) onRoutes(result.routes, renderer.getRouteIndex());
        });
        return () => listener.remove();
    }, [renderer, search.sequence, onRoutes]);

    useEffect(() => {
        if (!renderer || !routesLibrary || search.sequence === 0) return;
        let cancelled = false;
        renderer.setMap(map);
        new routesLibrary.DirectionsService().route({
            origin: search.origin,
            destination: search.destination,
            waypoints: search.waypoints.map((location) => ({ location, stopover: true })),
            travelMode: google.maps.TravelMode.DRIVING,
            provideRouteAlternatives: true,
        }).then((result) => {
            if (cancelled) return;
            renderer.setDirections(result);
            onRoutes(result.routes, 0);
            renderer.setRouteIndex(0);
        }).catch(() => {
            if (!cancelled) {
                renderer.setMap(null);
                onRoutes([]);
            }
        });
        return () => { cancelled = true; };
    }, [map, renderer, routesLibrary, search, onRoutes]);

    useEffect(() => { renderer?.setRouteIndex(selectedIndex); }, [renderer, selectedIndex]);

    useEffect(() => {
        if (search.sequence === 0) renderer?.setMap(null);
    }, [renderer, search.sequence]);

    useEffect(() => {
        if (!map || !geometry || !savedPolyline) return;
        const path = geometry.encoding.decodePath(savedPolyline);
        const line = new google.maps.Polyline({ map, path, strokeColor: "#0FB403", strokeWeight: 5 });
        const bounds = new google.maps.LatLngBounds();
        path.forEach((point) => bounds.extend(point));
        map.fitBounds(bounds);
        return () => line.setMap(null);
    }, [map, geometry, savedPolyline]);
    return null;
}

const routeSummary = (route: google.maps.DirectionsRoute) => {
    const distance = route.legs.reduce((sum, leg) => sum + (leg.distance?.value ?? 0), 0);
    const duration = route.legs.reduce((sum, leg) => sum + (leg.duration?.value ?? 0), 0);
    return `${(distance / 1000).toFixed(1)} km · ${Math.round(duration / 60)} min`;
};

const selectedPolyline = (route: google.maps.DirectionsRoute): string => {
    // Os pontos de cada etapa preservam curvas que a overview_polyline pode simplificar.
    const fullPath = route.legs.flatMap((leg) => leg.steps.flatMap((step) => step.path ?? []))
        .filter((point, index, points) => index === 0 || !point.equals(points[index - 1]));
    if (fullPath.length >= 2 && google.maps.geometry?.encoding) {
        return google.maps.geometry.encoding.encodePath(fullPath);
    }
    const overview = route.overview_polyline as unknown as string | { points?: string };
    if (typeof overview === "string") return overview;
    if (overview?.points) return overview.points;
    if (route.overview_path?.length && google.maps.geometry?.encoding) {
        return google.maps.geometry.encoding.encodePath(route.overview_path);
    }
    return "";
};

const RoutesPlannedTab = ({ isHiddenMenu }: { isHiddenMenu: boolean }) => {
    const [cookies] = useCookies(["token"]);
    const token: string = cookies.token || sessionStorage.getItem("token") || "";
    const user = useRecoilValue<UserType>(userAtom);
    const customerId = user.customer?.id ?? -1;
    const routePermission = user.permissions.find((item) => item.optionItem.id === Number(MENUS_ITENS.ROUTES));
    const canCreate = Boolean(routePermission?.canCreate);
    const canEdit = Boolean(routePermission?.canEdit);
    const [routes, setRoutes] = useState<PlannedRouteSummary[]>([]);
    const [vehicles, setVehicles] = useState<VehicleListType[]>([]);
    const [selectedRoute, setSelectedRoute] = useState<PlannedRoute | null>(null);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [origin, setOrigin] = useState<RouteAddress>(emptyAddress);
    const [destination, setDestination] = useState<RouteAddress>(emptyAddress);
    const [waypoints, setWaypoints] = useState<RouteWaypoint[]>([]);
    const [search, setSearch] = useState<Search>({ origin: "", destination: "", waypoints: [], sequence: 0 });
    const [alternatives, setAlternatives] = useState<google.maps.DirectionsRoute[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [vehicleId, setVehicleId] = useState<number>(-1);
    const [routeSchedules, setRouteSchedules] = useState<RouteSchedule[]>([]);
    const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5]);
    const [startTime, setStartTime] = useState("08:00");
    const [endTime, setEndTime] = useState("18:00");
    const [effectiveStart, setEffectiveStart] = useState(dayjs().format("YYYY-MM-DD"));
    const [effectiveEnd, setEffectiveEnd] = useState("");
    const [corridorMeters, setCorridorMeters] = useState<number | "">(150);
    const [pricePerLiter, setPricePerLiter] = useState("");
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(false);
    const [routeLoading, setRouteLoading] = useState(false);
    const [schedulesLoading, setSchedulesLoading] = useState(false);
    const [schedulesError, setSchedulesError] = useState(false);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTab, setModalTab] = useState(0);
    const [query, setQuery] = useState("");
    const [showActive, setShowActive] = useState(true);
    const [page, setPage] = useState(0);
    const openingRequest = useRef(0);
    const nextWaypointId = useRef(0);
    const [message, setMessage] = useState<{ type: "success" | "error" | "warn"; text: string } | null>(null);
    const pageToastRef = useRef<Toast>(null);
    const modalToastRef = useRef<Toast>(null);
    const confirmationToastRef = useRef<Toast>(null);
    const showAddressSearchError = () => setMessage({ type: "error", text: "Não foi possível pesquisar o endereço." });
    const scheduleValues = {
        days: [...days].sort().join(","), startTime, endTime, effectiveStart, effectiveEnd,
        corridorMeters, pricePerLiter: pricePerLiter.trim(),
    };
    const scheduleBaseline = useRef(scheduleValues);
    const hasScheduleChanges = JSON.stringify(scheduleValues) !== JSON.stringify(scheduleBaseline.current);
    const hasRouteDraft = Boolean(origin.text.trim() || destination.text.trim() || waypoints.length || search.sequence);
    const hasRouteChanges = name.trim() !== (selectedRoute?.name ?? "") ||
        description.trim() !== (selectedRoute?.description ?? "") || hasRouteDraft;

    useEffect(() => {
        if (!message) return;
        setToastRefToOpen(modalOpen ? modalToastRef : pageToastRef, message.text, message.type === "success" ? "Sucesso!" : "Atenção!", message.type, undefined, 3000, false);
        setMessage(null);
    }, [message, modalOpen]);

    const refresh = useCallback(async () => {
        if (customerId <= 0) return;
        setLoading(true);
        try {
            const [routeList, vehicleResult] = await Promise.all([
                routeApi.list(token, customerId),
                findAllVehiclesByCustomerIdAndSecretaryIdAndDepartmentIdListResponse(token, customerId, -1, true),
            ]);
            setRoutes(routeList);
            setVehicles(vehicleResult.data);
        } catch (error) {
            setMessage({ type: "error", text: getApiErrorMessage(error, "Não foi possível carregar as rotas.") });
        } finally { setLoading(false); }
    }, [token, customerId]);
    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        setRouteSchedules([]);
        setSchedulesError(false);
        if (!modalOpen || vehicleId <= 0 || customerId <= 0) { setSchedulesLoading(false); return; }
        let cancelled = false;
        setSchedulesLoading(true);
        routeApi.schedules(token, customerId, vehicleId)
            .then((data) => { if (!cancelled) setRouteSchedules(data); })
            .catch((error) => {
                if (!cancelled) {
                    setSchedulesError(true);
                    setMessage({ type: "error", text: getApiErrorMessage(error, "Não foi possível carregar as programações.") });
                }
            })
            .finally(() => { if (!cancelled) setSchedulesLoading(false); });
        return () => { cancelled = true; };
    }, [token, customerId, vehicleId, modalOpen]);

    const handleRoutes = useCallback((options: google.maps.DirectionsRoute[], routeIndex = 0) => {
        setAlternatives([...options]);
        setSelectedIndex(routeIndex >= 0 && routeIndex < options.length ? routeIndex : 0);
        setRouteLoading(false);
        if (options.length === 0) setMessage({ type: "error", text: "Nenhuma alternativa de rota foi encontrada." });
    }, []);
    const displayedPolyline = search.sequence === 0 && !hasRouteDraft ? selectedRoute?.encodedPolyline ?? null : null;
    const chosen = alternatives[selectedIndex];
    const routeNeedsCalculation = hasRouteDraft && !chosen;
    const filteredRoutes = useMemo(() => routes.filter((route) => route.activated === showActive && route.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())), [routes, showActive, query]);
    const pageRoutes = useMemo(() => filteredRoutes.slice(page * 100, (page + 1) * 100), [filteredRoutes, page]);

    const invalidateRoute = () => {
        setAlternatives([]);
        setSelectedIndex(0);
        setRouteLoading(false);
        setSearch({ origin: "", destination: "", waypoints: [], sequence: 0 });
    };

    const traceRoute = () => {
        if (busy || routeLoading || !GTR_MAPS_KEY || !origin.text.trim() || !destination.text.trim() ||
            waypoints.some(({ address }) => !address.text.trim())) return;
        setAlternatives([]);
        setSelectedIndex(0);
        setRouteLoading(true);
        setSearch((current) => ({
            origin: directionLocation(origin), destination: directionLocation(destination),
            waypoints: waypoints.map(({ address }) => directionLocation(address)),
            sequence: current.sequence + 1,
        }));
    };

    const chooseStored = (route: PlannedRoute | null, resetSchedule = true) => {
        setSelectedRoute(route);
        setName(route?.name ?? "");
        setDescription(route?.description ?? "");
        setOrigin(emptyAddress());
        setDestination(emptyAddress());
        setWaypoints([]);
        invalidateRoute();
        if (resetSchedule) {
            const initialDate = dayjs().format("YYYY-MM-DD");
            setVehicleId(-1);
            setRouteSchedules([]);
            setDays([1, 2, 3, 4, 5]);
            setStartTime("08:00");
            setEndTime("18:00");
            setEffectiveStart(initialDate);
            setEffectiveEnd("");
            setCorridorMeters(150);
            setPricePerLiter("");
            scheduleBaseline.current = { days: "1,2,3,4,5", startTime: "08:00", endTime: "18:00", effectiveStart: initialDate, effectiveEnd: "", corridorMeters: 150, pricePerLiter: "" };
        }
        setMessage(null);
    };

    const closeModal = () => {
        setModalOpen(false);
        setRouteLoading(false);
        modalToastRef.current?.clear();
        confirmationToastRef.current?.clear();
        setConfirmationOpen(false);
    };

    const verifyBeforeCancel = () => {
        if (busy || confirmationOpen) return;
        if (!hasRouteChanges && !hasScheduleChanges) { closeModal(); return; }
        modalToastRef.current?.clear();
        setConfirmationOpen(true);
        setToastRefToOpen(confirmationToastRef, "Existem dados não salvos, deseja realmente cancelar?", "Atenção!", "warn", {
            content: () => (
                <Box className="tw-flex tw-flex-1 tw-flex-col tw-gap-3 tw-font-prometo">
                    <Typography className="!tw-font-prometo !tw-font-bold">Atenção!</Typography>
                    <Typography className="!tw-font-prometo">Existem dados não salvos, deseja realmente cancelar?</Typography>
                    <Box className="tw-flex tw-justify-end tw-gap-2">
                        <Button onClick={closeModal} sx={{ color: "inherit", textTransform: "none" }}>Sim</Button>
                        <Button onClick={() => { confirmationToastRef.current?.clear(); setConfirmationOpen(false); }} sx={{ color: "inherit", textTransform: "none" }}>Não</Button>
                    </Box>
                </Box>
            ),
        }, undefined, true);
    };

    const openNew = () => {
        openingRequest.current += 1;
        chooseStored(null);
        setModalTab(0);
        setModalOpen(true);
    };

    const loadStored = async (routeId: number) => {
        const request = ++openingRequest.current;
        setBusy(true);
        try {
            const found = await routeApi.find(token, customerId, routeId);
            if (request !== openingRequest.current) return;
            chooseStored(found);
            setModalTab(0);
            setModalOpen(true);
        } catch (error) {
            if (request === openingRequest.current) setMessage({ type: "error", text: getApiErrorMessage(error, "Não foi possível abrir a rota.") });
        } finally { if (request === openingRequest.current) setBusy(false); }
    };

    const save = async () => {
        if (busy || routeLoading || confirmationOpen || !(selectedRoute ? canEdit : canCreate)) return;
        if (name.trim().length < 3 || name.trim().length > 150) {
            setMessage({ type: "warn", text: "Informe um nome de rota entre 3 e 150 caracteres." });
            return;
        }
        if (description.trim().length > 500) {
            setMessage({ type: "warn", text: "A descrição deve ter no máximo 500 caracteres." });
            return;
        }
        if (routeNeedsCalculation) {
            setMessage({ type: "warn", text: "Os pontos do trajeto foram alterados. Clique em Traçar rota antes de salvar." });
            return;
        }
        const route = chosen;
        const polyline = route ? selectedPolyline(route) : selectedRoute?.encodedPolyline ?? "";
        if (!name.trim() || !polyline) {
            setMessage({ type: "error", text: "Informe o nome e selecione uma alternativa no mapa." });
            return;
        }
        const request: PlannedRouteRequest = {
            customerId, departmentId: selectedRoute?.departmentId ?? null, name: name.trim(), description: description.trim() || null,
            encodedPolyline: polyline, source: "GOOGLE_DIRECTIONS",
            plannedDistanceMeters: route ? route.legs.reduce((sum, leg) => sum + (leg.distance?.value ?? 0), 0) : selectedRoute?.plannedDistanceMeters ?? null,
            plannedDurationSeconds: route ? route.legs.reduce((sum, leg) => sum + (leg.duration?.value ?? 0), 0) : selectedRoute?.plannedDurationSeconds ?? null,
            copyrights: route?.copyrights ?? selectedRoute?.copyrights ?? null,
        };
        if (selectedRoute && request.name === selectedRoute.name && request.description === selectedRoute.description &&
            request.encodedPolyline === selectedRoute.encodedPolyline && request.plannedDistanceMeters === selectedRoute.plannedDistanceMeters &&
            request.plannedDurationSeconds === selectedRoute.plannedDurationSeconds && request.copyrights === selectedRoute.copyrights) {
            setMessage({ type: "warn", text: "Nada foi alterado." });
            return;
        }
        setBusy(true);
        try {
            const isNew = !selectedRoute;
            const saved = selectedRoute
                ? await routeApi.update(token, selectedRoute.id, request)
                : await routeApi.create(token, request);
            await refresh();
            chooseStored(saved, false);
            setMessage({ type: "success", text: isNew ? "Rota salva. Agora você pode vincular veículos na aba Veículos e horários." : "Rota salva com sucesso." });
        } catch (error) {
            setMessage({ type: "error", text: getApiErrorMessage(error, "Não foi possível salvar a rota.") });
        } finally { setBusy(false); }
    };

    const assign = async () => {
        if (busy || schedulesLoading || confirmationOpen || !canCreate || !selectedRoute?.activated) return;
        if (vehicleId <= 0 || days.length === 0 || days.some((day) => day < 1 || day > 7)) {
            setMessage({ type: "warn", text: "Selecione um veículo e pelo menos um dia da semana." });
            return;
        }
        if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(startTime) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(endTime) || startTime === endTime) {
            setMessage({ type: "warn", text: "Informe horários válidos e diferentes para início e fim." });
            return;
        }
        if (!effectiveStart || dayjs(effectiveStart).format("YYYY-MM-DD") !== effectiveStart ||
            (effectiveEnd && (dayjs(effectiveEnd).format("YYYY-MM-DD") !== effectiveEnd || effectiveEnd < effectiveStart))) {
            setMessage({ type: "warn", text: "Informe uma vigência válida. A data final não pode ser anterior à inicial." });
            return;
        }
        if (corridorMeters === "" || !Number.isInteger(corridorMeters) || corridorMeters < 25 || corridorMeters > 2000) {
            setMessage({ type: "warn", text: "Informe um corredor entre 25 e 2.000 metros, sem casas decimais." });
            return;
        }
        const parsedPrice = pricePerLiter.trim() ? Number(pricePerLiter.replace(",", ".")) : null;
        if (parsedPrice !== null && (!Number.isFinite(parsedPrice) || parsedPrice < 0)) {
            setMessage({ type: "error", text: "Informe um valor por litro válido." });
            return;
        }
        setBusy(true);
        try {
            const saved = await routeApi.createSchedule(token, {
                customerId, routeId: selectedRoute.id, vehicleId, weekdays: days,
                startTime, endTime, effectiveStart, effectiveEnd: effectiveEnd || null,
                corridorMeters, pricePerLiter: parsedPrice,
            });
            setRouteSchedules((current) => [saved, ...current]);
            setSchedulesError(false);
            scheduleBaseline.current = scheduleValues;
            setMessage({ type: "success", text: "Programação vinculada ao veículo." });
        } catch (error) {
            setMessage({ type: "error", text: getApiErrorMessage(error, "Não foi possível vincular a rota.") });
        } finally { setBusy(false); }
    };

    const toggleRoute = async () => {
        if (!selectedRoute || !canEdit || busy || confirmationOpen) return;
        setBusy(true);
        try {
            const changed = await routeApi.setActive(token, customerId, selectedRoute.id, !selectedRoute.activated);
            setSelectedRoute(changed);
            await refresh();
            setMessage({ type: "success", text: changed.activated ? "Rota ativada." : "Rota desativada." });
        } catch (error) {
            setMessage({ type: "error", text: getApiErrorMessage(error, "Não foi possível alterar o status da rota.") });
        } finally { setBusy(false); }
    };

    const toggleSchedule = async (schedule: RouteSchedule) => {
        if (!canEdit || busy || schedulesLoading || confirmationOpen) return;
        setBusy(true);
        try {
            const updated = await routeApi.setScheduleActive(token, customerId, schedule.id, !schedule.activated);
            setRouteSchedules((current) => current.map((item) => item.id === updated.id ? updated : item));
            setMessage({ type: "success", text: "Status da programação atualizado." });
        } catch (error) {
            setMessage({ type: "error", text: getApiErrorMessage(error, "Não foi possível alterar a programação.") });
        } finally { setBusy(false); }
    };

    return (
        <>
            <Toast ref={pageToastRef} position="center" />
            <Box className="tw-h-full tw-min-h-0" sx={{
                "& .datagrid-selectable": { "--DataGrid-overlayHeight": "360px" },
            }}>
            <GridTemplate
                columns={columns} headerName="Rotas previstas" isHiddenMenu={isHiddenMenu}
                isLoading={loading || busy} order="asc" sortField="name" rows={pageRoutes}
                withFilter={true} withOptions={true} query={query} setQuery={(value) => { setQuery(value); setPage(0); }}
                filterPlaceholder="Nome da rota" disableReportButton={true} disableAddButton={!canCreate || customerId <= 0}
                onRefresh={(_: string, status?: string) => { setShowActive(status !== "deactivated"); setPage(0); void refresh(); }}
                onExport={async () => {}} onAdd={openNew} onClickRow={(row: any) => { void loadStored(Number(row.id)); }}
                handleNext={(_: string, nextPage: number) => setPage(Math.max(0, nextPage))} handlePrevious={() => {}}
                isLastPage={(page + 1) * 100 >= filteredRoutes.length} totalPerPage={100} currentPage={page}
                totalElements={filteredRoutes.length} containerHeight="100%"
                style={{ marginTop: 0, padding: 0, border: 0, borderRadius: 0, boxShadow: "none", background: "transparent" }}
            />
            </Box>
            <Rmodal open={modalOpen} handleClose={verifyBeforeCancel} toast={<><Toast ref={modalToastRef} position="center" /><Toast ref={confirmationToastRef} position="center" /></>}>
                <Paper elevation={0} className="report-map-modal tw-flex tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-border-0 tw-bg-gtr-surface tw-font-prometo tw-shadow-gtr-modal" sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: "calc(100vw - 16px)", md: "min(94vw, 1180px)" }, height: "min(88dvh, 800px)", display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: 4 }}>
                    <Box className="tw-flex tw-items-center tw-gap-3 tw-bg-gtr-sidebar tw-px-4 tw-py-3 tw-text-white sm:tw-px-5">
                        <Box className="tw-flex tw-h-10 tw-w-10 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-white/10"><RouteOutlined /></Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}><Typography className="!tw-font-prometo !tw-font-bold !tw-text-white" noWrap>{selectedRoute ? selectedRoute.name : "Nova rota prevista"}</Typography><Typography variant="caption" className="!tw-text-white/75">Cadastro e programação de rota</Typography></Box>
                        {selectedRoute && <Chip size="small" label={selectedRoute.activated ? "Ativa" : "Inativa"} color={selectedRoute.activated ? "success" : "default"} />}
                        <Tooltip title="Fechar" arrow><span><IconButton aria-label="Fechar" onClick={verifyBeforeCancel} disabled={busy || confirmationOpen} className="!tw-h-10 !tw-w-10 !tw-rounded-lg !tw-text-white hover:!tw-bg-white/10"><CloseOutlined /></IconButton></span></Tooltip>
                    </Box>
                    <Tooltip title={!selectedRoute ? "Salve a rota para vincular veículos e horários." : ""} arrow>
                        <Box>
                            <Tabs value={modalTab} onChange={(_, value) => { setModalTab(value); setMessage(null); }} className="tw-bg-gtr-surface-muted tw-px-2 sm:tw-px-4" sx={{ "& .MuiTabs-indicator": { backgroundColor: "#00B700" }, "& .Mui-selected": { color: "#00B700 !important" } }}>
                                <Tab label="Traçado" disabled={busy || routeLoading || confirmationOpen} sx={{ fontFamily: "Prometo", textTransform: "none" }} />
                                <Tab label="Veículos e horários" disabled={false} sx={{ fontFamily: "Prometo", textTransform: "none" }} />
                            </Tabs>
                        </Box>
                    </Tooltip>
                    <Box className="tw-bg-gtr-surface" sx={{ flex: 1, overflowY: "auto", p: { xs: 1.5, sm: 2 }, minHeight: 0 }}>
                        {modalTab === 0 ? (
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(260px, 32%) minmax(0, 1fr)" }, gap: 2, height: "100%", minHeight: { lg: 490 } }}>
                                <Stack spacing={2} sx={{ minWidth: 0 }}>
                                    <TextField label="Nome da rota" placeholder="Informe o nome da rota" InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 150 }} value={name} onChange={(event) => setName(event.target.value)} required fullWidth disabled={busy || confirmationOpen || !(selectedRoute ? canEdit : canCreate)} sx={modalFieldSx} />
                                    <TextField label="Descrição" placeholder="Descreva a rota" InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 500 }} value={description} onChange={(event) => setDescription(event.target.value)} fullWidth multiline minRows={2} disabled={busy || confirmationOpen || !(selectedRoute ? canEdit : canCreate)} sx={modalFieldSx} />
                                    <Typography variant="subtitle2" sx={{ color: "#006c77", fontWeight: 700 }}>Planejamento do trajeto</Typography>
                                    <RouteAddressAutocomplete label="Origem" placeholder="Pesquise o endereço de origem" address={origin} onChange={(address) => { setOrigin(address); invalidateRoute(); }} onSearchError={showAddressSearchError} disabled={busy || confirmationOpen || !(selectedRoute ? canEdit : canCreate)} sx={modalFieldSx} />
                                    {waypoints.map((waypoint, index) => <Box key={waypoint.id} sx={{ display: "flex", gap: 1, minWidth: 0 }}><RouteAddressAutocomplete label={`Parada ${index + 1}`} placeholder="Pesquise o endereço da parada" address={waypoint.address} onChange={(address) => { setWaypoints((current) => current.map((item) => item.id === waypoint.id ? { ...item, address } : item)); invalidateRoute(); }} onSearchError={showAddressSearchError} disabled={busy || confirmationOpen || !(selectedRoute ? canEdit : canCreate)} sx={{ ...modalFieldSx, flex: 1 }} /><Tooltip title="Remover parada" arrow><span><IconButton onClick={() => { setWaypoints((current) => current.filter((item) => item.id !== waypoint.id)); invalidateRoute(); }} disabled={busy || confirmationOpen || !(selectedRoute ? canEdit : canCreate)} aria-label={`Remover parada ${index + 1}`} className="!tw-bg-transparent !tw-text-gtr-teal dark:!tw-text-slate-200"><CloseOutlined /></IconButton></span></Tooltip></Box>)}
                                    <RouteAddressAutocomplete label="Destino" placeholder="Pesquise o endereço de destino" address={destination} onChange={(address) => { setDestination(address); invalidateRoute(); }} onSearchError={showAddressSearchError} disabled={busy || confirmationOpen || !(selectedRoute ? canEdit : canCreate)} sx={modalFieldSx} />
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                        <Button variant="outlined" startIcon={<AddOutlined />} disabled={busy || confirmationOpen || waypoints.length >= 8 || !(selectedRoute ? canEdit : canCreate)} onClick={() => { setWaypoints((current) => [...current, { id: nextWaypointId.current++, address: emptyAddress() }]); invalidateRoute(); }} sx={{ textTransform: "none", color: "#006C77", borderColor: "#006C77" }}>Adicionar parada</Button>
                                        <Button variant="contained" startIcon={routeLoading ? <CircularProgress size={17} color="inherit" /> : undefined} disabled={busy || routeLoading || confirmationOpen || !GTR_MAPS_KEY || !origin.text.trim() || !destination.text.trim() || waypoints.some(({ address }) => !address.text.trim()) || !(selectedRoute ? canEdit : canCreate)} onClick={traceRoute} sx={primaryButtonSx}>{routeLoading ? "Calculando..." : "Traçar rota"}</Button>
                                    </Stack>
                                    {selectedRoute && !hasRouteDraft && <Typography variant="caption" color="text.secondary">Traçado salvo exibido no mapa. Para substituí-lo, informe novos pontos e clique em “Traçar rota”.</Typography>}
                                    {routeNeedsCalculation && !routeLoading && <Typography variant="caption" className="!tw-text-gtr-text-muted" role="status">Clique em “Traçar rota” para calcular o percurso dos pontos informados antes de salvar.</Typography>}
                                    {!selectedRoute && <Typography variant="caption" className="!tw-text-gtr-text-muted">Após salvar, a aba “Veículos e horários” ficará disponível para a programação.</Typography>}
                                    {alternatives.length > 0 && <Box><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Escolha o traçado</Typography><RadioGroup value={selectedIndex} onChange={(event) => setSelectedIndex(Number(event.target.value))}>{alternatives.map((option, index) => <FormControlLabel key={index} value={index} control={<Radio size="small" />} label={`Alternativa ${index + 1} — ${routeSummary(option)}`} />)}</RadioGroup><Typography variant="caption" color="text.secondary">Arraste o traçado no mapa para ajustar o percurso antes de salvar.</Typography></Box>}
                                    {(chosen?.copyrights || selectedRoute?.copyrights) && <Typography variant="caption">{chosen?.copyrights || selectedRoute?.copyrights}</Typography>}
                                </Stack>
                                <Box sx={{ position: "relative", minWidth: 0, minHeight: { xs: 360, lg: 490 }, borderRadius: 2, overflow: "hidden" }} aria-busy={routeLoading}>
                                    {GTR_MAPS_KEY ? <APIProvider apiKey={GTR_MAPS_KEY} libraries={["geometry"]} onError={() => { setRouteLoading(false); setMessage({ type: "error", text: "Não foi possível carregar o mapa. Tente novamente." }); }}><Map mapId={GTR_MAPS_ID} defaultCenter={{ lat: -15.6, lng: -56.1 }} defaultZoom={6} style={{ width: "100%", height: "100%" }}><MapRoutes search={search} onRoutes={handleRoutes} selectedIndex={selectedIndex} savedPolyline={displayedPolyline} editable={!busy && !routeLoading && !confirmationOpen && (selectedRoute ? canEdit : canCreate)} /></Map></APIProvider> : <Alert severity="warning">Chave do mapa não configurada.</Alert>}
                                    {routeLoading && <Box className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-gtr-surface/80 tw-text-gtr-text" role="status"><CircularProgress size={22} color="inherit" />Calculando trajeto...</Box>}
                                </Box>
                            </Box>
                        ) : selectedRoute && (
                            <Stack spacing={2} component="fieldset" disabled={busy || confirmationOpen} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
                                <Typography variant="body2" color="text.secondary">Selecione um veículo para vincular esta rota e consultar suas programações.</Typography>
                                <TextField select label="Veículo" placeholder="Selecione um veículo" InputLabelProps={{ shrink: true }} value={vehicleId} onChange={(event) => { setRouteSchedules([]); setSchedulesLoading(Number(event.target.value) > 0); setVehicleId(Number(event.target.value)); }} disabled={busy || confirmationOpen} fullWidth sx={{ ...modalFieldSx, maxWidth: 500 }}><MenuItem value={-1}>Selecione um veículo</MenuItem>{vehicles.map((vehicle) => <MenuItem key={vehicle.id} value={vehicle.id}>{`${vehicle.name} - ${vehicle.vehiclePlate} (${vehicle.deviceImei ?? vehicle.satelliteImei ?? "Sem dispositivo"})`}</MenuItem>)}</TextField>
                                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0,1fr))", lg: "repeat(4, minmax(0,1fr))" }, gap: 2 }}>
                                        <MobileTimePicker label="Horário inicial" ampm={false} disabled={busy || confirmationOpen} value={startTime ? dayjs(`2000-01-01T${startTime}`) : null} onChange={(value) => setStartTime(value?.isValid() ? value.format("HH:mm") : "")} slotProps={{ textField: { fullWidth: true, placeholder: "Selecione o horário inicial", InputLabelProps: { shrink: true }, sx: modalFieldSx } }} />
                                        <MobileTimePicker label="Horário final" ampm={false} disabled={busy || confirmationOpen} value={endTime ? dayjs(`2000-01-01T${endTime}`) : null} onChange={(value) => setEndTime(value?.isValid() ? value.format("HH:mm") : "")} slotProps={{ textField: { fullWidth: true, placeholder: "Selecione o horário final", InputLabelProps: { shrink: true }, sx: modalFieldSx } }} />
                                        <MobileDatePicker label="Vigência inicial" disabled={busy || confirmationOpen} value={effectiveStart ? dayjs(effectiveStart) : null} onChange={(value) => setEffectiveStart(value?.isValid() ? value.format("YYYY-MM-DD") : "")} slotProps={{ textField: { fullWidth: true, placeholder: "Selecione a data inicial", InputLabelProps: { shrink: true }, sx: modalFieldSx } }} />
                                        <MobileDatePicker label="Vigência final (opcional)" disabled={busy || confirmationOpen} minDate={effectiveStart ? dayjs(effectiveStart) : undefined} value={effectiveEnd ? dayjs(effectiveEnd) : null} onChange={(value) => setEffectiveEnd(value?.isValid() ? value.format("YYYY-MM-DD") : "")} slotProps={{ textField: { fullWidth: true, placeholder: "Selecione a data final", InputLabelProps: { shrink: true }, sx: modalFieldSx } }} />
                                    </Box>
                                </LocalizationProvider>
                                {startTime && endTime && endTime < startTime && <Typography variant="caption" className="!tw-text-gtr-text-muted">O horário final será no dia seguinte ao início da programação.</Typography>}
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>{weekdays.map((label, index) => <FormControlLabel key={label} label={label} control={<Checkbox checked={days.includes(index + 1)} onChange={() => setDays((current) => current.includes(index + 1) ? current.filter((day) => day !== index + 1) : [...current, index + 1])} />} />)}</Box>
                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0,1fr))" }, gap: 2 }}>
                                    <TextField label="Corredor (m)" placeholder="Informe o corredor" InputLabelProps={{ shrink: true }} type="number" value={corridorMeters} onChange={(event) => setCorridorMeters(event.target.value === "" ? "" : Number(event.target.value))} inputProps={{ min: 25, max: 2000, step: 1 }} sx={modalFieldSx} />
                                    <TextField label="Valor por litro (opcional)" placeholder="Informe o valor por litro" InputLabelProps={{ shrink: true }} value={pricePerLiter} onChange={(event) => setPricePerLiter(event.target.value)} sx={modalFieldSx} />
                                </Box>
                                <Button variant="contained" onClick={assign} startIcon={busy ? <CircularProgress size={17} color="inherit" /> : undefined} disabled={busy || confirmationOpen || schedulesLoading || !canCreate || !selectedRoute.activated || vehicleId <= 0 || days.length === 0} sx={{ ...primaryButtonSx, alignSelf: "flex-start" }}>Vincular rota ao veículo</Button>
                                {vehicleId > 0 && <Paper elevation={0} className="tw-rounded-lg tw-border-0 tw-bg-gtr-surface-muted tw-shadow-none" sx={{ overflowX: "auto" }}><Table size="small"><TableHead><TableRow><TableCell>Horário</TableCell><TableCell>Dias</TableCell><TableCell>Vigência</TableCell><TableCell>Status</TableCell><TableCell>Ação</TableCell></TableRow></TableHead><TableBody>
                                    {routeSchedules.filter((schedule) => schedule.routeId === selectedRoute.id).map((schedule) => <TableRow key={schedule.id}><TableCell>{schedule.startTime.slice(0, 5)}–{schedule.endTime.slice(0, 5)}</TableCell><TableCell>{schedule.weekdays.map((day) => weekdays[day - 1]).join(", ")}</TableCell><TableCell>{dayjs(schedule.effectiveStart).format("DD/MM/YYYY")} – {schedule.effectiveEnd ? dayjs(schedule.effectiveEnd).format("DD/MM/YYYY") : "sem fim"}</TableCell><TableCell><Chip size="small" color={schedule.activated ? "success" : "default"} label={schedule.activated ? "Ativa" : "Inativa"} /></TableCell><TableCell><Button disabled={!canEdit || busy} onClick={() => { void toggleSchedule(schedule); }}>{schedule.activated ? "Desativar" : "Ativar"}</Button></TableCell></TableRow>)}
                                    {schedulesLoading ? <TableRow><TableCell colSpan={5}><Box className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-3" role="status"><CircularProgress size={20} color="inherit" />Carregando programações...</Box></TableCell></TableRow> : routeSchedules.every((schedule) => schedule.routeId !== selectedRoute.id) && <TableRow><TableCell colSpan={5}>{schedulesError ? "Não foi possível carregar as programações. Selecione o veículo novamente para tentar." : "Nenhuma programação desta rota para o veículo selecionado."}</TableCell></TableRow>}
                                </TableBody></Table></Paper>}
                            </Stack>
                        )}
                    </Box>
                    <Box className="tw-flex tw-flex-wrap tw-justify-end tw-gap-2 tw-border-t tw-border-gtr-border/60 tw-bg-gtr-surface tw-px-4 tw-py-3 sm:tw-px-5">
                        {modalTab === 0 && selectedRoute && <Button variant="outlined" onClick={toggleRoute} disabled={busy || routeLoading || confirmationOpen || !canEdit} sx={{ textTransform: "none", borderColor: "#006C77", color: "#006C77", fontFamily: "Prometo" }}>{selectedRoute.activated ? "Desativar" : "Ativar"}</Button>}
                        <Button variant="contained" className="login-button-close" startIcon={<CloseOutlined />} onClick={verifyBeforeCancel} disabled={busy || confirmationOpen}>Fechar</Button>
                        {modalTab === 0 && <Button variant="contained" className="login-button" startIcon={busy ? <CircularProgress size={17} color="inherit" /> : <SaveOutlined />} onClick={save} disabled={busy || routeLoading || confirmationOpen || !(selectedRoute ? canEdit : canCreate)}>Salvar rota</Button>}
                    </Box>
                </Paper>
            </Rmodal>
        </>
    );
};

export default RoutesPlannedTab;
